import type { AnyRouter } from "@tanstack/react-router";
import { reloadForStaleChunk } from "./reload-for-stale-chunk";
import { useAppVersionStore } from "./use-app-version-store";

/**
 * Auto code splitting gives every route its own content-hashed chunk, so a
 * document served by an older deployment 404s on any route it has not loaded
 * yet. Arm the passive update, and when the user is actually going somewhere,
 * load the destination as a new document.
 *
 * On an idle (`defaultPreload: "intent"` hover) failure there is nowhere to go
 * yet, so only the update is armed. The click that follows is normally
 * intercepted by `subscribeToPendingUpdate`; when it is not (a masked or
 * same-path navigation, an in-flight write, a lost `acquireRefresh` race), the
 * rejected import reaches an error boundary and `ErrorFallback` recovers there.
 *
 * Never call `event.preventDefault()` here. Vite reads it as "handled" and
 * resolves the import with `undefined` instead of rejecting, so
 * `lazyRouteComponent` reads `undefined["component"]` and throws a TypeError
 * nothing downstream can recognise as a missing module — which turns a
 * recoverable stale chunk into the full-screen "Something went wrong" fallback
 * on every deploy. Letting the rejection stand is what arms every recovery.
 */
export function recoverFromStaleChunk(router: AnyRouter): () => void {
  const handlePreloadError = () => {
    useAppVersionStore.getState().actions.markUpdatePending();

    if (router.state.status !== "pending") return;

    reloadForStaleChunk(router.state.location.href);
  };

  window.addEventListener("vite:preloadError", handlePreloadError);
  return () =>
    window.removeEventListener("vite:preloadError", handlePreloadError);
}
